import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Assistente interno — o chat da Home sobre os dados da plataforma.
 *
 * Não confundir com `ChatService` (conversas com clientes no WhatsApp) nem com
 * `AgentTesterService` (ensaio do agente que atende cliente). Contrato completo
 * em `docs/requisicoes-http/assistente.md` no repositório da API.
 */

export type EscopoAssistente = 'empresa' | 'minha_carteira';

export interface ConversaAssistente {
  conversaId: number;
  titulo: string;
  escopo: EscopoAssistente;
  criadaEm: string;
  atualizadaEm: string;
}

/**
 * Um gráfico que acompanha a resposta.
 *
 * Montado pela ferramenta que consultou o dado, no servidor — **não pelo
 * modelo**. É o que garante que o gráfico não desenhe uma série diferente da
 * que o texto da resposta descreve.
 */
export interface VisualAssistente {
  /** Hoje só `serie_temporal`. */
  tipo: 'serie_temporal';
  titulo: string;
  /** Nome curto da série, para o tooltip ("Faturado"). */
  rotuloSerie?: string;
  /** `moeda` formata em R$; `numero` mostra o valor cru. */
  formato: 'moeda' | 'numero';
  pontos: { rotulo: string; valor: number }[];
}

/**
 * Uma ação que o assistente fez ou propôs, gravada no livro de ações da API
 * (`assistant_actions`). É o que a bolha desenha como cartão.
 *
 * `proposta` espera o clique: o modelo nunca confirma sozinho — quem executa é
 * o endpoint `Acoes/{id}/Confirmar`. O estado é do servidor e volta junto da
 * mensagem, então o cartão sobrevive a recarregar a conversa.
 */
export type EstadoAcaoAssistente = 'proposta' | 'feita' | 'cancelada' | 'expirada' | 'desfeita' | 'falhou';

export interface LinhaAcaoAssistente {
  rotulo: string;
  /** Valor antes da ação; ausente quando a linha só informa. */
  de?: string | null;
  para: string;
}

export interface AcaoAssistente {
  acaoId: number;
  /** `entidade.acao`, ex.: `oportunidade.ganhar`, `tarefa.criar`. */
  tipo: string;
  titulo: string;
  linhas: LinhaAcaoAssistente[];
  estado: EstadoAcaoAssistente;
  /** Só verdadeiro quando o servidor sabe desfazer E nada mudou no registro desde então. */
  podeDesfazer: boolean;
  /** Ação âmbar que não tem volta (criar oportunidade): o cartão avisa antes do Confirmar. */
  semDesfazer?: boolean;
  /** Para onde o "Abrir" leva (ex.: `/pipeline/deal/171`). */
  rota?: string | null;
  expiraEm?: string | null;
  atualizadaEm: string;
  /** Motivo, quando a execução falhou ou o desfazer foi recusado. */
  erro?: string | null;
}

/**
 * Onde a pessoa está quando pergunta pelo painel. A API confere o id contra o
 * que o perfil alcança antes de usar — mandar um id qualquer não abre nada.
 *
 * `rota` é o endereço da tela (10/10/2026): a API manda junto o artigo de
 * ajuda dela, e "o que posso fazer nessa tela?" passa a ter resposta.
 */
export interface ContextoTelaAssistente {
  tipo?: 'oportunidade';
  id?: number;
  rota?: string;
}

/**
 * Avisa as telas abertas que uma ação do assistente mudou um registro, para
 * elas recarregarem o que mostram. `detail` diz qual entidade e qual id.
 */
export const ACAO_ASSISTENTE_EVENT = 'lemeia:assistente-acao';

export const avisarAcaoDoAssistente = (acao: AcaoAssistente) => {
  const [entidade] = acao.tipo.split('.');
  window.dispatchEvent(new CustomEvent(ACAO_ASSISTENTE_EVENT, { detail: { entidade, tipo: acao.tipo, rota: acao.rota } }));
};

/**
 * Uma coisa que o Brik AI leu nas conversas e aponta (21/10/2026): venda que já
 * fechou, etapa que devia avançar ou conversa que não é negócio. Nada foi
 * alterado: o cartão prova e aponta o caminho.
 */
export interface AchadoFunil {
  /** A oportunidade: o nº que a tela mostra e o id da rota. */
  oportunidadeId: number;
  contato: string;
  etapa: string;
  responsavel?: string | null;
  /** Do rastro do servidor: é por ele que o botão "Mover" age. */
  achadoId: number;
  /**
   * `ganha`/`provavel` = venda fechada na conversa · `etapa` = devia estar mais
   * à frente no funil · `fora_de_venda` = nem é negócio.
   */
  tipo: 'ganha' | 'provavel' | 'etapa' | 'fora_de_venda';
  /** Para onde o Brik sugere mover; só nos achados de etapa. */
  etapaSugeridaId?: number | null;
  etapaSugerida?: string | null;
  /** A frase do cliente, copiada da conversa — o servidor confere que ela existe mesmo. */
  prova: string;
  /** O valor escrito na conversa; ausente quando ninguém falou preço. */
  valorSugerido?: number | null;
  /** De onde saiu o valor ("19 da peça + 8 da entrega"). */
  contaDoValor?: string | null;
  rota: string;
}

/**
 * Uma conversa que a varredura NÃO conseguiu julgar porque é só áudio, foto ou
 * vídeo (25/10/2026). Não é achado: não há nada que o Brik tenha concluído, e
 * por isso não há botão que aja — só o caminho para a pessoa ir ouvir.
 */
export interface ConversaSemTexto {
  oportunidadeId: number;
  contato: string;
  etapa: string;
  responsavel?: string | null;
  /** Quantas mensagens a varredura leu, e quantas delas tinham texto. */
  mensagens: number;
  comTexto: number;
  audios: number;
  imagens: number;
  videos: number;
  ultimaMensagemEm?: string | null;
  rota: string;
}

/**
 * Uma tela que a resposta ensinou a usar (25/10/2026). Quem escolhe é o
 * servidor, pelo campo `telas` do artigo lido — o modelo não inventa rota.
 */
export interface AtalhoDeTela {
  titulo: string;
  /** Onde ela fica, em palavras ("Configurações › Chatbot › Produtos e serviços"). */
  onde: string;
  rota: string;
}

/** O resultado de mover vários cartões de uma vez. */
export interface MoverEmLoteResultado {
  /** Uma ação por oportunidade movida — cada uma com o seu próprio desfazer. */
  movidas: AcaoAssistente[];
  falhas: { achadoId: number; contato: string; motivo: string }[];
}

export interface MensagemAssistente {
  mensagemId: number;
  /** `user` é o que a pessoa escreveu; `model`, o que o assistente respondeu. */
  papel: 'user' | 'model';
  conteudo: string;
  /** Gráfico da resposta, quando a consulta rendeu um. */
  visual?: VisualAssistente | null;
  /**
   * Rótulos do que foi consultado ("Gestão de vendas · setembro/2026 · 3
   * vendedores"). Quem escreve é o servidor, a partir das consultas que
   * realmente rodaram — por isso dá para mostrar como fonte sem ressalva.
   */
  fontes: string[];
  /** Só nas respostas: 1 útil, -1 não útil, nulo sem avaliação. */
  avaliacao?: 1 | -1 | null;
  /** O que o assistente fez ou propôs nesta resposta, na ordem. */
  acoes?: AcaoAssistente[];
  /** O que a varredura do funil apontou nesta resposta. */
  achados?: AchadoFunil[];
  /**
   * As conversas que a varredura não pôde julgar. Vêm fora de `achados` de
   * propósito: não são cartão — a tela mostra um link com o número e a lista
   * abre num diálogo.
   */
  semTexto?: ConversaSemTexto[];
  /** As telas citadas na resposta, para abrir sem procurar no menu. */
  atalhos?: AtalhoDeTela[];
  criadaEm: string;
}

/**
 * De onde veio a pergunta. `sugestao` é o clique numa sugestão pronta da Home;
 * sem isso, o "mais perguntado" da tela Sistema › Assistente mediria as
 * sugestões e não o que as pessoas escrevem.
 */
export type OrigemPergunta = 'digitada' | 'sugestao';

export interface ConversaComMensagens {
  conversa: ConversaAssistente;
  mensagens: MensagemAssistente[];
}

export interface RespostaPergunta {
  conversa: ConversaAssistente;
  pergunta: MensagemAssistente;
  resposta: MensagemAssistente;
  /**
   * Verdadeiro quando o texto é a mensagem de contingência (a consulta falhou,
   * ou o modelo não fechou a resposta) — a tela não pode mostrar isso como se
   * fosse análise.
   */
  degradada: boolean;
}

/** A cara do Brik no balão da Home, decidida pela API (desenho da Home, seção 6). */
export type HumorBrik = 'feliz' | 'comemorando' | 'dormindo';

/**
 * O balão do Brik AI na Home (`GET Assistente/Abertura`, 07/10/2026). Montado por
 * regra fixa no servidor, sem o modelo — por isso chega junto com a página.
 * Desenho: `docs/designsystem/propostas/home-brik.html`.
 */
export interface AberturaAssistente {
  /** A frase do balão: até três fatos e uma pergunta, com negrito em markdown (`**…**`). */
  fala: string;
  /**
   * Os mesmos fatos numa linha, para a faixa do Brik no topo da conversa (07/10/2026).
   * Opcional: a API de antes não manda, e a faixa usa a fala sem o negrito.
   */
  resumo?: string;
  /** A visita anterior à Home, ou nulo na primeira. */
  desde: string | null;
  humor: HumorBrik;
  /** Até três perguntas — sempre consultas que o Brik responde, nunca ação. */
  sugestoes: string[];
  /** Falso: a Home mostra a apresentação do Brik antes do balão. */
  apresentacaoVista: boolean;
  /** A empresa ainda não tem oportunidade nenhuma: o balão aponta os Primeiros passos. */
  contaNova: boolean;
}

/**
 * Avisa quem mostra a lista de conversas (a Sidebar) que ela mudou. A Sidebar
 * carrega as conversas uma vez; sem este evento, uma conversa criada na Home só
 * apareceria no próximo recarregamento da página.
 */
export const CONVERSAS_ASSISTENTE_EVENT = 'lemeia:assistente-conversas';

export const avisarConversasMudaram = () =>
  window.dispatchEvent(new CustomEvent(CONVERSAS_ASSISTENTE_EVENT));

/**
 * Lê a resposta sem confiar que ela é JSON.
 *
 * `res.json()` direto foi o que vazou "Unexpected end of JSON input" para o
 * usuário: uma API sem estes endpoints responde **404 com corpo vazio**, e o
 * parse estoura antes de qualquer tratamento. Toda resposta passa por aqui, e o
 * erro sai em português dizendo o que fazer.
 */
async function lerResposta<T>(res: Response): Promise<T> {
  const texto = await res.text();

  if (!texto) {
    if (res.status === 404) {
      throw new Error(
        'O assistente não existe nesta API (404). A API apontada em VITE_API_URL precisa estar na versão com o assistente.',
      );
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error('Sua sessão expirou. Entre de novo para continuar.');
    }
    throw new Error(`O servidor respondeu ${res.status} sem conteúdo.`);
  }

  let corpo: any;
  try {
    corpo = JSON.parse(texto);
  } catch {
    /* HTML de página de erro, texto de proxy, qualquer coisa que não seja o
       envelope do projeto. Mostrar o começo ajuda a identificar sem despejar
       uma página inteira no toast. */
    throw new Error(`Resposta inesperada do servidor (${res.status}): ${texto.slice(0, 120)}`);
  }

  if (!res.ok || corpo?.sucesso === false) {
    throw new Error(corpo?.mensagem || `O servidor respondeu ${res.status}.`);
  }

  return corpo.dados as T;
}

/**
 * Lê o `PerguntarStream` (Server-Sent Events): `texto` acrescenta, `limpar`
 * zera (o que apareceu era o modelo anunciando uma consulta, não a resposta),
 * `consultando` diz quais ferramentas vão rodar (07/10/2026, para a tela dizer o
 * que o Brik está fazendo; a API de antes manda sem a lista) e `fim` traz o mesmo
 * envelope do `Perguntar` — é ele que vale e é ele que a tela guarda. O texto
 * parcial só adianta a leitura.
 */
async function lerFluxo(
  res: Response,
  aoEscrever: (parcial: string) => void,
  aoConsultar?: (ferramentas: string[]) => void,
): Promise<RespostaPergunta> {
  if (!res.ok || !res.body) return lerResposta<RespostaPergunta>(res);

  const leitor = res.body.getReader();
  const decodificador = new TextDecoder();
  let pendente = '';
  let parcial = '';
  type Envelope = { sucesso?: boolean; mensagem?: string; dados?: RespostaPergunta };
  let fim = null as Envelope | null;

  for (;;) {
    const { value, done } = await leitor.read();
    if (done) break;
    pendente += decodificador.decode(value, { stream: true });

    let corte: number;
    while ((corte = pendente.indexOf('\n\n')) >= 0) {
      const bloco = pendente.slice(0, corte).replace(/^data:\s?/, '');
      pendente = pendente.slice(corte + 2);
      if (!bloco) continue;

      const evento = JSON.parse(bloco) as { tipo: string; texto?: string; ferramentas?: string[] | null; resposta?: Envelope };
      if (evento.tipo === 'texto' && evento.texto) {
        parcial += evento.texto;
        aoEscrever(parcial);
      } else if (evento.tipo === 'limpar') {
        parcial = '';
        aoEscrever(parcial);
      } else if (evento.tipo === 'consultando') {
        aoConsultar?.(Array.isArray(evento.ferramentas) ? evento.ferramentas : []);
      } else if (evento.tipo === 'fim') {
        fim = evento.resposta ?? null;
      }
    }
  }

  if (!fim) throw new Error('A conexão com o assistente caiu antes do fim da resposta. Tente de novo.');
  if (fim.sucesso === false || !fim.dados) throw new Error(fim.mensagem || 'Não foi possível responder agora.');
  return fim.dados;
}

const AssistenteService = {
  /**
   * O balão do Brik na Home. A virada do dia vai daqui, como no sino: é o
   * navegador que sabe o fuso de quem está olhando. Também marca a visita — o
   * "desde" da próxima abertura sai daqui.
   */
  abertura: async (): Promise<AberturaAssistente> => {
    const virada = new Date();
    virada.setHours(0, 0, 0, 0);
    const res = await apiFetch(`${API_URL}/api/Assistente/Abertura?viradaDoDia=${encodeURIComponent(virada.toISOString())}`);
    return lerResposta<AberturaAssistente>(res);
  },

  listarConversas: async (limite = 30): Promise<ConversaAssistente[]> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Conversas?limite=${limite}`);
    return (await lerResposta<ConversaAssistente[]>(res)) ?? [];
  },

  buscarConversa: async (id: number): Promise<ConversaComMensagens> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Conversas/${id}`);
    return lerResposta<ConversaComMensagens>(res);
  },

  /**
   * O alcance não vai no corpo: desde 18/09 não há seletor de escopo, e a API
   * decide o que responder pelo perfil do usuário no token — o `escopo` do
   * contrato continua existindo lá, opcional, mas o front não manda mais.
   */
  perguntar: async (
    pergunta: string,
    conversaId?: number,
    signal?: AbortSignal,
    origem: OrigemPergunta = 'digitada',
    contexto?: ContextoTelaAssistente | null,
    /**
     * Recebe o texto da resposta enquanto o modelo escreve (sempre o texto
     * inteiro até ali, não o trecho). Sem ele, a resposta chega só no fim.
     */
    aoEscrever?: (parcial: string) => void,
    /** Recebe as ferramentas que vão rodar, a cada rodada de consulta do modelo. */
    aoConsultar?: (ferramentas: string[]) => void,
  ): Promise<RespostaPergunta> => {
    const corpo = JSON.stringify({ conversaId: conversaId ?? null, pergunta, origem, contexto: contexto ?? null });

    if (aoEscrever) {
      const res = await apiFetch(`${API_URL}/api/Assistente/PerguntarStream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: corpo,
        signal,
      });
      /* API anterior ao streaming (07/10/2026): 404 → cai no Perguntar de sempre. */
      if (res.status !== 404) return lerFluxo(res, aoEscrever, aoConsultar);
    }

    const res = await apiFetch(`${API_URL}/api/Assistente/Perguntar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: corpo,
      signal,
    });
    return lerResposta<RespostaPergunta>(res);
  },

  /** Executa a ação proposta. A API revalida alcance, dono e validade antes de executar. */
  confirmarAcao: async (acaoId: number): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Acoes/${acaoId}/Confirmar`, { method: 'POST' });
    return lerResposta<AcaoAssistente>(res);
  },

  cancelarAcao: async (acaoId: number): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Acoes/${acaoId}/Cancelar`, { method: 'POST' });
    return lerResposta<AcaoAssistente>(res);
  },

  /** Recusa (com o motivo em `erro`) quando o registro mudou depois da ação. */
  /**
   * O clique em "Mover para X" num cartão de etapa. O servidor reconfere o
   * alcance e a régua de avanço e devolve a ação — é dela que vem o desfazer.
   */
  moverPeloAchado: async (achadoId: number): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/${achadoId}/Mover`, { method: 'POST' });
    return lerResposta<AcaoAssistente>(res);
  },

  /**
   * O clique em "Mover as N selecionadas". O servidor move uma a uma e devolve
   * o que foi e o que não foi — uma recusa no meio não cancela as outras.
   */
  moverAchadosEmLote: async (achadoIds: number[]): Promise<MoverEmLoteResultado> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/Mover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ achadoIds }),
    });
    return lerResposta<MoverEmLoteResultado>(res);
  },

  /**
   * "Tirar do funil" num cartão de conversa que não é negócio. A marca é no
   * contato: as conversas dele ficam no Chat e saem da Pipeline e das métricas.
   */
  tirarDoFunilPeloAchado: async (achadoId: number): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/${achadoId}/TirarDoFunil`, { method: 'POST' });
    return lerResposta<AcaoAssistente>(res);
  },

  tirarDoFunilEmLote: async (achadoIds: number[]): Promise<MoverEmLoteResultado> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/TirarDoFunil`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ achadoIds }),
    });
    return lerResposta<MoverEmLoteResultado>(res);
  },

  /**
   * O clique em "Dar como ganho" num cartão de venda fechada (25/10/2026). O
   * `valor` é o que a pessoa confirmou no cartão; omitido, vale o que o Brik
   * leu na conversa. Sem nenhum dos dois o servidor recusa — venda sem valor
   * entraria zerada no faturamento.
   */
  ganharPeloAchado: async (achadoId: number, valor?: number | null): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/${achadoId}/Ganhar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ valor: valor ?? null }),
    });
    return lerResposta<AcaoAssistente>(res);
  },

  /** "Dar as N como ganhas". O valor vai por item: cada conversa tem o seu. */
  ganharAchadosEmLote: async (itens: { achadoId: number; valor?: number | null }[]): Promise<MoverEmLoteResultado> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Achados/Ganhar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itens }),
    });
    return lerResposta<MoverEmLoteResultado>(res);
  },

  desfazerAcao: async (acaoId: number): Promise<AcaoAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Acoes/${acaoId}/Desfazer`, { method: 'POST' });
    return lerResposta<AcaoAssistente>(res);
  },

  /** Útil (1), não útil (-1) ou desfaz (null), numa resposta da própria conversa. */
  avaliar: async (mensagemId: number, avaliacao: 1 | -1 | null): Promise<void> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Mensagens/${mensagemId}/Avaliacao`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ avaliacao }),
    });
    await lerResposta<unknown>(res);
  },

  renomear: async (id: number, titulo: string): Promise<ConversaAssistente> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Conversas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ titulo }),
    });
    return lerResposta<ConversaAssistente>(res);
  },

  remover: async (id: number): Promise<void> => {
    const res = await apiFetch(`${API_URL}/api/Assistente/Conversas/${id}`, { method: 'DELETE' });
    await lerResposta<unknown>(res);
  },
};

export default AssistenteService;
